import React, {useEffect, useRef, useState} from "react";
import cn from "classnames";
import {useSceneScope} from "hooks/useSceneScope";
import {DIMENSIONS, type DimensionId} from "libs/sceneScope";

/**
 * The chain, whole or broken. Drawn here rather than loaded from the CDN
 * `sync.svg` / `unsync.svg` pair: those carry a "T" badge for the text lock
 * that uses them, and a scopes chain speaks for every dimension, not text.
 * Geometry after Lucide's `link` / `unlink` (ISC).
 */
const Chain = ({broken}: {broken: boolean}) => (
  <svg
    className="scope-chips__chain"
    width="25"
    height="25"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {broken ? (
      <>
        <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
        <path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
        <path d="M8 2v3" />
        <path d="M2 8h3" />
        <path d="M16 19v3" />
        <path d="M19 16h3" />
      </>
    ) : (
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    )}
  </svg>
);

/**
 * A banner's standing on each dimension, in its header: one chip per
 * dimension — hollow for a banner that follows the shared value, dotted for
 * a preset from the defaults, filled for a value of the banner's own. Click
 * opens the rows: state, reset to default, reset to shared, make global,
 * detach. Replaces the chain on a scopes channel.
 */
const STATE_LABEL: Record<string, string> = {
  own: "own",
  preset: "preset",
  inherit: "shared",
};

const ScopeChips = ({id}: {id: string}) => {
  const scope = useSceneScope();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!scope.scoped) return null;

  const stateOf = (dimension: DimensionId) => scope.modeOf(id, dimension) ?? "inherit";
  const summary = DIMENSIONS.map((d) => `${d.label}: ${STATE_LABEL[stateOf(d.id)]}`).join(" · ");

  const ownCount = DIMENSIONS.filter((d) => stateOf(d.id) === "own").length;
  const presetCount = DIMENSIONS.filter((d) => stateOf(d.id) === "preset").length;

  return (
    <div className="scope-chips" ref={rootRef}>
      {/* The same 40px circle as the eye beside it. The badge in its corner
          says the most a banner diverges: filled for a value of its own,
          dotted for a preset, nothing when it follows the shared scene. */}
      <button
        type="button"
        className={cn("checkbox sync scope-chips__button", {
          "sync--own": ownCount > 0,
          "sync--preset": ownCount === 0 && presetCount > 0,
        })}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={`Sync state: ${summary}`}
        data-tooltip={summary}
        data-own={ownCount}
        data-preset={presetCount}
      >
        {/* The house chain: whole while the banner follows the scene, broken
            once it holds a value of its own. */}
        <Chain broken={ownCount > 0} />
        <span className="sync-badge" aria-hidden="true" />
      </button>
      {open && (
        <div className="scope-chips__popover" role="dialog" aria-label="Sync state">
          {DIMENSIONS.map((d) => {
            const state = stateOf(d.id);
            return (
              <div key={d.id} className="scope-chips__row">
                <span className={cn("scope-chip", `scope-chip--${state}`)} aria-hidden="true">
                  {d.glyph}
                </span>
                <span className="scope-chips__name">{d.label}</span>
                <span className={cn("scope-chips__state", `scope-chips__state--${state}`)}>
                  {STATE_LABEL[state]}
                </span>
                <span className="scope-chips__actions">
                  {state !== "own" && (
                    <button
                      type="button"
                      onClick={() => scope.detach(id, d.id)}
                      title={
                        state === "preset"
                          ? "Keep the preset as this banner's own: shared changes no longer take it"
                          : "Set this banner apart on this dimension"
                      }
                    >
                      Detach
                    </button>
                  )}
                  {state !== "inherit" && (
                    <>
                      <button
                        type="button"
                        onClick={() => scope.resetTemplate(id, d.id, "default")}
                        title="Back to the default's value for this banner, or to the shared value"
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={() => scope.resetTemplate(id, d.id, "global")}
                        title="Back to the shared value"
                      >
                        To shared
                      </button>
                      {d.id !== "image" && (
                        <button
                          type="button"
                          onClick={() => scope.makeGlobal(id, d.id)}
                          title="Make this banner's value the shared one"
                        >
                          Make global
                        </button>
                      )}
                    </>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScopeChips;
